import React from 'react';

import {
  BannerRoot,
  SummaryBlock,
  IconBadge,
  SummaryText,
  Title,
  Subtitle,
  Divider,
  Description,
} from './styles';

export interface InfoBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Ícone exibido no badge à esquerda. @default ícone de calendário */
  icon?: React.ReactNode;
  /** Título em destaque (ex: "Previsão de abastecimento"). */
  title: React.ReactNode;
  /** Subtítulo abaixo do título (ex: "Sexta, 01/05/2026"). */
  subtitle?: React.ReactNode;
  /** Texto descritivo à direita. Aceita `<strong>` para destaques. */
  children: React.ReactNode;
  /** Exibe o divisor vertical entre resumo e descrição. @default true */
  showDivider?: boolean;
}

const CalendarIcon = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3.5"
      y="4.5"
      width="17"
      height="15"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M3.5 9h17"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    {/* 6 pontos (3×2), como no Figma */}
    <circle cx="8" cy="12.5" r="1.1" fill="currentColor" />
    <circle cx="12" cy="12.5" r="1.1" fill="currentColor" />
    <circle cx="16" cy="12.5" r="1.1" fill="currentColor" />
    <circle cx="8" cy="16" r="1.1" fill="currentColor" />
    <circle cx="12" cy="16" r="1.1" fill="currentColor" />
    <circle cx="16" cy="16" r="1.1" fill="currentColor" />
  </svg>
);

/**
 * InfoBanner — banner informativo que combina um resumo em destaque
 * (ícone + título + subtítulo) com um texto explicativo, separados por
 * um divisor vertical.
 *
 * Usado na "Previsão de abastecimento" (Figma node 10187-55151), mas
 * genérico o suficiente para qualquer par "fato em destaque + explicação".
 *
 * @example
 * <InfoBanner
 *   title="Previsão de abastecimento"
 *   subtitle="Sexta, 01/05/2026"
 * >
 *   A data indicada corresponde à <strong>previsão</strong> do seu
 *   abastecimento. Recomendamos que <strong>confirme</strong> a programação.
 * </InfoBanner>
 */
export const InfoBanner = ({
  icon,
  title,
  subtitle,
  children,
  showDivider = true,
  ...rest
}: InfoBannerProps): React.ReactElement => {
  return (
    <BannerRoot role="note" {...rest}>
      <SummaryBlock>
        <IconBadge aria-hidden="true">{icon ?? <CalendarIcon />}</IconBadge>
        <SummaryText>
          <Title>{title}</Title>
          {subtitle !== undefined && <Subtitle>{subtitle}</Subtitle>}
        </SummaryText>
      </SummaryBlock>

      {showDivider && <Divider aria-hidden="true" />}

      <Description>{children}</Description>
    </BannerRoot>
  );
};

export default InfoBanner;
